import React, { useState } from 'react';
import axios from '../config/axios';

interface ExportOptionsProps {
  entityType: 'users' | 'contracts' | 'pricing-headers' | 'audit-logs';
  filters?: { [key: string]: any };
  searchQuery?: string;
}

const ExportOptions: React.FC<ExportOptionsProps> = ({
  entityType,
  filters = {},
  searchQuery = ''
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'excel' | 'pdf' | 'csv'>('excel');
  const [includeSummary, setIncludeSummary] = useState(true);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      
      // Add filters as query parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      if (searchQuery) {
        params.append('q', searchQuery);
      }
      
      params.append('include_summary', includeSummary.toString());

      const url = `/api/v1/export/${entityType}/${exportFormat}?${params.toString()}`;
      
      // Create a temporary link to download the file
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Set filename based on format
      const timestamp = new Date().toLocaleString().replace(/[/\?%*:|"<>]/g, '-');
      const extension = exportFormat === 'excel' ? 'xlsx' : exportFormat;
      link.download = `${entityType}_export_${timestamp}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCustomExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('entity_type', entityType);
      params.append('include_summary', includeSummary.toString());
      
      if (searchQuery) {
        params.append('query', searchQuery);
      }
      
      if (Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }

      const url = `/api/v1/export/custom/${exportFormat}?${params.toString()}`;
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const timestamp = new Date().toLocaleString().replace(/[/\?%*:|"<>]/g, '-');
      const extension = exportFormat === 'excel' ? 'xlsx' : exportFormat;
      link.download = `${entityType}_custom_export_${timestamp}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (error) {
      console.error('Custom export error:', error);
      alert('Error exporting data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4">Export Options</h3>
      
      <div className="space-y-4">
        {/* Format Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Export Format
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="excel"
                checked={exportFormat === 'excel'}
                onChange={(e) => setExportFormat(e.target.value as 'excel')}
                className="mr-2"
              />
              Excel (.xlsx)
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="pdf"
                checked={exportFormat === 'pdf'}
                onChange={(e) => setExportFormat(e.target.value as 'pdf')}
                className="mr-2"
              />
              PDF
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="csv"
                checked={exportFormat === 'csv'}
                onChange={(e) => setExportFormat(e.target.value as 'csv')}
                className="mr-2"
              />
              CSV
            </label>
          </div>
        </div>

        {/* Include Summary Option */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={includeSummary}
              onChange={(e) => setIncludeSummary(e.target.checked)}
              className="mr-2"
            />
            Include summary statistics
          </label>
        </div>

        {/* Export Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? 'Exporting...' : `Export as ${exportFormat.toUpperCase()}`}
          </button>
          
          {(searchQuery || Object.keys(filters).length > 0) && (
            <button
              onClick={handleCustomExport}
              disabled={isExporting}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? 'Exporting...' : 'Export Filtered Data'}
            </button>
          )}
        </div>

        {/* Export Info */}
        <div className="text-sm text-gray-600">
          <p>
            <strong>Excel:</strong> Best for data analysis and manipulation
          </p>
          <p>
            <strong>PDF:</strong> Best for reports and presentations
          </p>
          <p>
            <strong>CSV:</strong> Best for importing into other systems
          </p>
          
          {(searchQuery || Object.keys(filters).length > 0) && (
            <p className="mt-2 text-blue-600">
              Current filters and search will be applied to the export
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;